import { useState, useEffect } from 'react';
import { apiService } from '../../../services/apiService';
import { StudentCluster, Suggestion, AnalyticsData } from '../../../types';

export const useAnalytics = (classId: string): AnalyticsData => {
  const [studentClusters, setStudentClusters] = useState<StudentCluster[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAnalyticsData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const { data: studentsData } = await apiService.getStudentProfilesByClass(classId);
        
        if (studentsData && studentsData.length > 0) {
          const clusters = generateStudentClusters(studentsData);
          setStudentClusters(clusters);
        } else {
          setStudentClusters([]);
        }
      } catch (err) {
        console.error('Failed to load analytics data:', err);
        setError('Failed to load analytics data');
        setStudentClusters([]);
      } finally {
        setLoading(false);
      }
    };

    if (classId) {
      loadAnalyticsData();
    }
  }, [classId]);

  // Generate student clusters from actual data
  const generateStudentClusters = (studentsData: any[]): StudentCluster[] => {
    if (studentsData.length === 0) return [];

    const clusters: { [key: string]: any[] } = {
      analytical: [],
      collaborative: [],
      creative: []
    };

    // Categorize students based on their responses
    studentsData.forEach(enrollment => {
      const responses = enrollment.studentProfile?.responses;
      if (!responses) return;

      let analyticalScore = 0;
      let collaborativeScore = 0;
      let creativeScore = 0;

      // Score based on learning preferences
      if (responses.learning_preference === 'structured') analyticalScore += 3;
      if (responses.learning_preference === 'visual') creativeScore += 2;
      if (responses.learning_preference === 'hands-on') creativeScore += 2;
      if (responses.communication_style === 'collaborative') collaborativeScore += 3;
      if (responses.academic_background === 'stem') analyticalScore += 2;
      if (responses.academic_background === 'arts') creativeScore += 2;
      if (responses.participation_level === 'high') collaborativeScore += 1;
      if (responses.feedback_preference === 'detailed') analyticalScore += 1;

      // Assign to dominant cluster
      if (analyticalScore >= collaborativeScore && analyticalScore >= creativeScore) {
        clusters.analytical.push(enrollment);
      } else if (collaborativeScore >= creativeScore) {
        clusters.collaborative.push(enrollment);
      } else {
        clusters.creative.push(enrollment);
      }
    });

    const result: StudentCluster[] = [];

    if (clusters.analytical.length > 0) {
      result.push({
        id: 'analytical-achievers',
        name: 'Analytical Achievers',
        size: clusters.analytical.length,
        characteristics: [
          'Prefer structured learning environments',
          'Excel in individual work and technical tasks',
          'Respond well to data-driven feedback',
          'Value clear expectations and detailed instructions'
        ],
        color: '#3B82F6',
        suggestions: [
          'Provide detailed rubrics and clear expectations',
          'Offer advanced supplementary materials',
          'Include peer tutoring opportunities',
          'Use project-based assessments with technical components'
        ]
      });
    }

    if (clusters.collaborative.length > 0) {
      result.push({
        id: 'collaborative-communicators',
        name: 'Collaborative Communicators',
        size: clusters.collaborative.length,
        characteristics: [
          'Strong interpersonal skills',
          'Prefer group work and discussions',
          'High participation levels',
          'Thrive in interactive learning environments'
        ],
        color: '#10B981',
        suggestions: [
          'Facilitate regular group projects and peer learning',
          'Create opportunities for cross-cultural exchange',
          'Use discussion-based assessment methods',
          'Implement collaborative problem-solving activities'
        ]
      });
    }

    if (clusters.creative.length > 0) {
      result.push({
        id: 'creative-explorers',
        name: 'Creative Explorers',
        size: clusters.creative.length,
        characteristics: [
          'Prefer flexible, open-ended assignments',
          'Strong creative and critical thinking skills',
          'Value personal expression and innovation',
          'Learn best through hands-on experiences'
        ],
        color: '#8B5CF6',
        suggestions: [
          'Offer choice in assignment topics and formats',
          'Include creative project options',
          'Encourage innovative approaches to problem-solving',
          'Provide opportunities for artistic integration'
        ]
      });
    }

    return result;
  };

  // Generate dynamic pedagogical suggestions based on student data
  const generateSuggestions = (clusters: StudentCluster[]): Suggestion[] => {
    const totalStudents = clusters.reduce((sum, cluster) => sum + cluster.size, 0);
    
    if (totalStudents === 0) {
      return [{
        category: 'Getting Started',
        icon: null,
        priority: 'High',
        insight: 'No student data available yet.',
        recommendation: 'Encourage students to complete their learning profiles to get personalized suggestions.',
        evidence: 'Personalized learning approaches show significant improvements in student engagement.',
        implementation: [
          'Share class access QR code with students',
          'Explain the benefits of completing the learning profile',
          'Set a deadline for profile completion'
        ]
      }];
    }

    const analyticalCluster = clusters.find(c => c.id === 'analytical-achievers');
    const collaborativeCluster = clusters.find(c => c.id === 'collaborative-communicators');
    const creativeCluster = clusters.find(c => c.id === 'creative-explorers');
    
    const analyticalPercentage = analyticalCluster ? Math.round((analyticalCluster.size / totalStudents) * 100) : 0;
    const collaborativePercentage = collaborativeCluster ? Math.round((collaborativeCluster.size / totalStudents) * 100) : 0;
    const creativePercentage = creativeCluster ? Math.round((creativeCluster.size / totalStudents) * 100) : 0;

    const suggestions: Suggestion[] = [];

    if (analyticalPercentage > 30) {
      suggestions.push({
        category: 'Structured Learning',
        icon: null,
        priority: 'High',
        insight: `${analyticalPercentage}% of students prefer structured learning with clear objectives and detailed instructions.`,
        recommendation: 'Implement detailed rubrics and structured learning pathways to support analytical learners.',
        evidence: 'Research shows structured learning environments improve performance by 25% for analytical learners.',
        implementation: [
          'Provide detailed assignment rubrics and expectations',
          'Offer step-by-step learning guides',
          'Include technical supplementary materials',
          'Use project-based assessments with clear criteria'
        ]
      });
    }

    if (collaborativePercentage > 25) {
      suggestions.push({
        category: 'Collaborative Learning',
        icon: null,
        priority: 'High',
        insight: `${collaborativePercentage}% of students thrive in collaborative and interactive learning environments.`,
        recommendation: 'Create more group work opportunities and peer learning activities.',
        evidence: 'Collaborative learning methods show 0.59 effect size on student achievement.',
        implementation: [
          'Implement regular group projects',
          'Use peer review and feedback systems',
          'Create discussion-based learning activities',
          'Facilitate cross-cultural learning exchanges'
        ]
      });
    }

    if (creativePercentage > 20) {
      suggestions.push({
        category: 'Creative Expression',
        icon: null,
        priority: 'Medium',
        insight: `${creativePercentage}% of students value creative expression and flexible learning approaches.`,
        recommendation: 'Offer choice-based assignments and creative project options.',
        evidence: 'Student choice in assignments increases motivation and engagement by 40%.',
        implementation: [
          'Provide multiple assignment format options',
          'Include creative project alternatives',
          'Allow personal expression in coursework',
          'Integrate artistic and innovative elements'
        ]
      });
    }

    // Add general suggestion for mixed classes
    if (clusters.length > 1) {
      suggestions.push({
        category: 'Differentiated Instruction',
        icon: null,
        priority: 'High',
        insight: `Your class has ${clusters.length} distinct learning preference groups requiring differentiated approaches.`,
        recommendation: 'Implement Universal Design for Learning (UDL) principles to accommodate all learning styles.',
        evidence: 'UDL approaches improve learning outcomes for 95% of students regardless of learning preferences.',
        implementation: [
          'Offer multiple ways to access content (visual, auditory, hands-on)',
          'Provide various ways to demonstrate learning',
          'Include multiple engagement strategies in each lesson',
          'Use flexible assessment methods'
        ]
      });
    }

    return suggestions;
  };

  const suggestions = generateSuggestions(studentClusters);

  return {
    studentClusters,
    suggestions,
    loading,
    error
  };
}; 